export interface SheetColumn {
    ordinalPosition?: number;
    key: string;
    header: string;
}

export interface SheetConfig {
    name: string;
    data: unknown[];
    columns: SheetColumn[];
}

export function exportToXlsx(fileName: string, sheets: SheetConfig[]): void {
    const files = buildXlsxFiles(sheets);
    const zip = createZip(files);
    downloadFile(
        fileName,
        zip,
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    );
}

// XLSX (XML) generation
function buildXlsxFiles(
    sheets: SheetConfig[],
): { path: string; content: string }[] {
    const files: { path: string; content: string }[] = [];

    // This part of the process signals to Excel that it's an XLSX file
    const sheetContentTypes = sheets
        .map(
            (_, i) =>
                `<Override PartName="/xl/worksheets/sheet${i + 1}.xml" 
                ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`,
        )
        .join("");

    // [Content_Types].xml provides MIME types for each part of the XLSX package
    // which are defined below. This is required for Excel to recognize the file as a valid XLSX document
    files.push({
        path: "[Content_Types].xml",
        content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
            <Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
                <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
                <Default Extension="xml" ContentType="application/xml"/>
                <Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
                <Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
                ${sheetContentTypes}
            </Types>`,
    });

    // _rels/.rels as defined in [Content_Types].xml
    files.push({
        path: "_rels/.rels",
        content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
            <Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
                <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
            </Relationships>`,
    });

    // xl/_rels/workbook.xml.rels as defined in [Content_Types].xml
    const sheetRels = sheets
        .map(
            (_, i) =>
                `<Relationship Id="rId${i + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" 
            Target="worksheets/sheet${i + 1}.xml"/>`,
        )
        .join("");

    // xl/_rels/workbook.xml.rels as defined in [Content_Types].xml continued
    files.push({
        path: "xl/_rels/workbook.xml.rels",
        content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
            <Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
                ${sheetRels}
                <Relationship Id="rId${sheets.length + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" 
                Target="styles.xml"/>
            </Relationships>`,
    });

    // xl/workbook.xml as defined in [Content_Types].xml
    const sheetEntries = sheets
        .map(
            (s, i) =>
                `<sheet name="${escapeXml(s.name)}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`,
        )
        .join("");

    // xl/workbook.xml as defined in [Content_Types].xml continued
    files.push({
        path: "xl/workbook.xml",
        content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
        <workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"
        xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
        <sheets>${sheetEntries}</sheets>
        </workbook>`,
    });

    // xl/styles.xml (minimal = plain cell formatting, no styles)
    // TODO: modify this to add styling options (e.g. bold headers, number formats, etc.)
    files.push({
        path: "xl/styles.xml",
        content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
        <styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
            <fonts count="1"><font><sz val="11"/><name val="Calibri"/></font></fonts>
            <fills count="1"><fill><patternFill patternType="none"/></fill></fills>
            <borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>
            <cellStyleXfs count="1"><xf/></cellStyleXfs>
            <cellXfs count="1"><xf/></cellXfs>
        </styleSheet>`,
    });

    // xl/worksheets/sheet{n}.xml as defined in [Content_Types].xml
    sheets.forEach((sheet, sheetIndex) => {
        const rows: string[] = [];

        // Header row
        const headerCells = sheet.columns
            .map(
                (col, colIndex) =>
                    `<c r="${colRef(colIndex)}1" t="inlineStr"><is><t>${escapeXml(col.header)}</t></is></c>`,
            )
            .join("");
        rows.push(`<row r="1">${headerCells}</row>`);

        // Data rows
        sheet.data.forEach((row, rowIndex) => {
            const obj = row as { [key: string]: unknown };
            // rowINdex is 0 based, Excel is 1 based. Add 2 to skip header row
            const rowNum = rowIndex + 2;
            const cells = sheet.columns
                .map((col, colIndex) => {
                    const value = obj[col.key];
                    if (typeof value === "number") {
                        return `<c r="${colRef(colIndex)}${rowNum}"><v>${value}</v></c>`;
                    }
                    return `<c r="${colRef(colIndex)}${rowNum}" t="inlineStr"><is><t>${escapeXml(String(value ?? ""))}</t></is></c>`;
                })
                .join("");
            rows.push(`<row r="${rowNum}">${cells}</row>`);
        });

        // sheetIndex is 0 based, sheets are 1 based. Add 1 to get the correct sheet number
        files.push({
            path: `xl/worksheets/sheet${sheetIndex + 1}.xml`,
            content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
            <worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
                <sheetData>${rows.join("")}</sheetData>
            </worksheet>`,
        });
    });

    return files;
}

// Convert col ordinal position to Excel based 26 column reference
// (0 -> A, 25 -> Z, 26 -> AA, etc.)
function colRef(index: number): string {
    let ref = "";
    let n = index;
    while (n >= 0) {
        const remainder = n % 26;
        // Add 65 to convert 0-25 to ASCII A-Z
        // If index greater than 25, concatenate letters (e.g. 26 -> AA)
        ref = String.fromCharCode(remainder + 65) + ref;
        // If index is greater than 25, loop again to get the next letter
        n = Math.floor(n / 26) - 1;
    }
    return ref;
}

// Escape XML special characters in cell values and headers
function escapeXml(value: string): string {
    return value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&apos;");
}

// ZIP file specification:
// https://www.loc.gov/preservation/digital/formats/fdd/fdd000354.shtml
// https://pkwaredownloads.blob.core.windows.net/pkware-general/Documentation/APPNOTE-6.2.0.txt

// Just zip files, no need for compression
function createZip(files: { path: string; content: string }[]): Uint8Array {
    const encoder = new TextEncoder();
    // Take data in XML format and encode it as UTF-8 bytes to get ready for ZIP packing.
    const entries = files.map((f) => ({
        path: encoder.encode(f.path),
        data: encoder.encode(f.content),
    }));

    // Calculate total size of the file to be generated
    let localHeadersSize = 0;
    let centralHeadersSize = 0;

    // ZIP format needs a local header, a central header, and an end of central header for each file
    // Each entry has a 30 byte header + XML path length + XML data length
    // Each entry has a 46 byte central header + XML path length
    for (const entry of entries) {
        localHeadersSize += 30 + entry.path.length + entry.data.length;
        centralHeadersSize += 46 + entry.path.length;
    }

    // Total file size is sum of header sizes + 22 byte end of central directory
    const totalSize = localHeadersSize + centralHeadersSize + 22;
    const buffer = new ArrayBuffer(totalSize);
    const view = new DataView(buffer); // Structur to write bytes at specific locations in the buffer
    const bytes = new Uint8Array(buffer); // Uint8Array to hold the actual byte data for the ZIP file

    let localOffset = 0;
    const centralEntries: {
        offset: number;
        path: Uint8Array;
        data: Uint8Array;
    }[] = [];

    // Write local file headers + data
    // Offset moves the pointer forward to write the next file in the ZIP archive
    // Store data for central directory which will be accessed later
    for (const entry of entries) {
        centralEntries.push({
            offset: localOffset,
            path: entry.path,
            data: entry.data,
        });

        // Write the local file header up to 30 bytes
        // First parameter is the pointer offset, second is the value to be pushed, third is little-endian flag (true for little-endian)
        view.setUint32(localOffset, 0x04034b50, true); // Local file header signature
        view.setUint16(localOffset + 4, 20, true); // Version needed to extract
        view.setUint16(localOffset + 6, 0, true); // General purpose bit flag
        view.setUint16(localOffset + 8, 0, true); // Compression Method: STORE
        view.setUint16(localOffset + 10, 0, true); // Last Mod file time
        view.setUint16(localOffset + 12, 0, true); // Last Mod file date
        view.setUint32(localOffset + 14, crc32(entry.data), true); // CRC-32
        view.setUint32(localOffset + 18, entry.data.length, true); // Compressed size = uncompressed since we're only storing not compressing
        view.setUint32(localOffset + 22, entry.data.length, true); // Uncompressed size
        view.setUint16(localOffset + 26, entry.path.length, true); // File name length
        view.setUint16(localOffset + 28, 0, true); // Extra field length. Not implemented, so set to 0

        // Write the entry path and data after the 30 byte header
        bytes.set(entry.path, localOffset + 30);
        bytes.set(entry.data, localOffset + 30 + entry.path.length);

        // Move the offset forward
        localOffset += 30 + entry.path.length + entry.data.length;
    }

    // Write central directory
    const centralStart = localOffset;

    // Write the 46 byte central directory header for each entry
    for (const entry of centralEntries) {
        view.setUint32(localOffset, 0x02014b50, true); // Central file header signature
        view.setUint16(localOffset + 4, 20, true); // Version made by
        view.setUint16(localOffset + 6, 20, true); // Version needed to extract
        view.setUint16(localOffset + 8, 0, true); // General purpose bit flag
        view.setUint16(localOffset + 10, 0, true); // Compression method: STORE
        view.setUint16(localOffset + 12, 0, true); // Last Mod file time
        view.setUint16(localOffset + 14, 0, true); // Last Mod file date
        view.setUint32(localOffset + 16, crc32(entry.data), true); // CRC-32
        view.setUint32(localOffset + 20, entry.data.length, true); // Compressed size = uncompressed since we're only storing not compressing
        view.setUint32(localOffset + 24, entry.data.length, true); // Uncompressed size
        view.setUint16(localOffset + 28, entry.path.length, true); // File name length
        view.setUint16(localOffset + 30, 0, true); // Extra field length. Not implemented, so set to 0
        view.setUint16(localOffset + 32, 0, true); // Comment length. Not implemented, so set to 0
        view.setUint16(localOffset + 34, 0, true); // Disk number. Not supporting multi-disk ZIPs, so set to 0
        view.setUint16(localOffset + 36, 0, true); // Internal file attributes. Not implemented, so set to 0
        view.setUint32(localOffset + 38, 0, true); // External file attributes. Not implemented, so set to 0
        view.setUint32(localOffset + 42, entry.offset, true); // Relative offset of local header

        // Write the entry path after the 46 byte header
        bytes.set(entry.path, localOffset + 46);

        // Move the offset forward
        localOffset += 46 + entry.path.length;
    }

    // End of central directory record
    const centralSize = localOffset - centralStart;
    view.setUint32(localOffset, 0x06054b50, true); // End of central directory signature
    view.setUint16(localOffset + 4, 0, true); // Disk number. Not supporting multi-disk ZIPs, so set to 0
    view.setUint16(localOffset + 6, 0, true); // Disk number where the central directory starts. Not supporting multi-disk ZIPs, so set to 0
    view.setUint16(localOffset + 8, entries.length, true); // Total number of entries in the central directory on this disk
    view.setUint16(localOffset + 10, entries.length, true); // Total entries in the central directory
    view.setUint32(localOffset + 12, centralSize, true); // Central directory size
    view.setUint32(localOffset + 16, centralStart, true); // Central directory start offset
    view.setUint16(localOffset + 20, 0, true); // Comment length. Not implemented, so set to 0

    return bytes;
}

// ZIP requires a CRC-32 checksum for each file entry
// 32-bit cyclic redundancy check algorithm to generate a checksum for the file data
function crc32(data: Uint8Array): number {
    // Initialize CRC to all 1s
    let crc = 0xffffffff;
    // Process each byte in the data
    for (let i = 0; i < data.length; i++) {
        // XOR the byte with the current CRC value
        crc ^= data[i];
        // For each of the 8 bits in the byte
        // shift the CRC right and XOR with the polynomial if the least significant bit is 1
        for (let j = 0; j < 8; j++) {
            // Shift right by 1 bit. If the least significant bit is 1, XOR with the polynomial 0xedb88320
            crc = (crc >>> 1) ^ (crc & 1 ? 0xedb88320 : 0);
        }
    }

    // Finalize the CRC by inverting all bits and ensuring it's an unsigned 32-bit integer
    return (crc ^ 0xffffffff) >>> 0;
}

// Download the file
function downloadFile(
    fileName: string,
    data: Uint8Array,
    mimeType: string,
): void {
    const buffer = new ArrayBuffer(data.byteLength);
    new Uint8Array(buffer).set(data);
    const blob = new Blob([buffer], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);
}